import { forwardRef, type MouseEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAuthStore } from "src/store/authStore";
import { useLogout } from "src/hooks/useAuth";
import { usePortalMe } from "src/hooks/usePortal";
import { usePortalAuthStore } from "src/store/portalAuthStore";
import { useHoverDropdown } from "src/hooks/useHoverDropdown";
import { useBuilderDefaultDashboard } from "src/hooks/useBuilderDefaultDashboard";
import BuilderDashboardNavDropdown from "src/components/common/BuilderDashboardNavDropdown";
import { closeMobileNav } from "src/helpers/responsive";
import BuilderUserMenu from "src/components/common/BuilderUserMenu";
import DarkThemeToggle from "src/components/common/DarkThemeToggle";
import { SiteLogo } from "../../assets";

type ToggleButtonProps = {
  children: ReactNode;
  className?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

const UserMenuToggle = forwardRef<HTMLButtonElement, ToggleButtonProps>(
  ({ children, className, onClick }, ref) => (
    <button
      ref={ref}
      type="button"
      className={className}
      onClick={(event) => {
        event.preventDefault();
        onClick?.(event);
      }}
    >
      {children}
    </button>
  ),
);

UserMenuToggle.displayName = "UserMenuToggle";

export default function BuilderHeader() {
  // Which dropdown (by id) is currently open — a single key shared by every
  // dropdown in this header. Same pattern as AuthHeader.tsx.
  const { openKey, open, scheduleClose, setOpenKey } = useHoverDropdown();

  const navigate = useNavigate();
  const { user: userData, refreshToken } = useAuthStore();
  const isPortalSession = usePortalAuthStore((s) => s.isPortalSession);
  const { data: portalMe } = usePortalMe(isPortalSession);
  const isSuperAdmin = portalMe?.role === "super_admin";
  const { mutate: logout, isPending: isLoggingOut } = useLogout();

  const {
    allDashboards,
    myDashboards,
    selectedDashboardCode,
    setSelectedDashboardCode,
    setDefault,
  } = useBuilderDefaultDashboard(!!userData);

  const goToDashboard = (code: string) => {
    setSelectedDashboardCode(code);
    navigate(`/builder/${code}`);
  };

  return (
      <nav className="navbar navbar-expand-lg py-2 py-lg-0 position-sticky top-0">
        <div className="container-fluid px-3 px-lg-5 gap-2 gap-lg-0">
          <Link
              className="navbar-brand text-decoration-none p-0"
              to={"/dashboards"}
          >
            <img src={SiteLogo} alt="Analytics Elusa Health logo" />
          </Link>

          <div className="d-flex align-items-center gap-2 d-lg-none">
            <ul className="navbar-nav">
              <DarkThemeToggle />
            </ul>
            <button
                className="navbar-toggler"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#authHeader"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
          </div>

          <div className="collapse navbar-collapse ms-0 ms-lg-3" id="authHeader">
            <ul className="navbar-nav me-auto">
              {allDashboards?.default?.map((section) => {
                const dropdownKey = `builder-section-${section.code ?? section.type}`;
                return (
                    <BuilderDashboardNavDropdown
                        key={dropdownKey}
                        dropdownKey={dropdownKey}
                        label={section.name}
                        dashboards={section.dashboards}
                        selectedDashboardCode={selectedDashboardCode}
                        toggleAs={UserMenuToggle}
                        openKey={openKey}
                        onOpen={open}
                        onScheduleClose={scheduleClose}
                        onSetOpenKey={setOpenKey}
                        onNavigate={(code) => {
                    goToDashboard(code);
                    closeMobileNav();
                  }}
                        onSetDefault={setDefault}
                    />
                );
              })}
              <BuilderDashboardNavDropdown
                  dropdownKey="builder-section-my-dashboards"
                  label="My Dashboards"
                  dashboards={myDashboards}
                  selectedDashboardCode={selectedDashboardCode}
                  toggleAs={UserMenuToggle}
                  openKey={openKey}
                  onOpen={open}
                  onScheduleClose={scheduleClose}
                  onSetOpenKey={setOpenKey}
                  onNavigate={(code) => {
                    goToDashboard(code);
                    closeMobileNav();
                  }}
                  onSetDefault={setDefault}
                  emptyMessage="No dashboards found"
              />
            </ul>

            <ul className="navbar-nav navbar-end">
              <div className="d-none d-lg-flex align-items-center">
                <DarkThemeToggle />
              </div>

              {!userData ? (
                  <div className="placeholder-glow">
            <span
                className="placeholder"
                style={{ width: "160px", height: "30px" }}
            ></span>
                  </div>
              ) : (
                  <BuilderUserMenu
                      userData={userData}
                      isSuperAdmin={isSuperAdmin}
                      toggleAs={UserMenuToggle}
                      openKey={openKey}
                      onOpen={open}
                      onScheduleClose={scheduleClose}
                      onSetOpenKey={setOpenKey}
                      onNavigateToActiveDashboard={() =>
                          navigate(
                              selectedDashboardCode
                                  ? `/dashboards/${selectedDashboardCode}`
                                  : "/dashboards",
                          )
                      }
                      onLogout={() => logout(refreshToken || "")}
                      isLoggingOut={isLoggingOut}
                  />
              )}
            </ul>
          </div>
        </div>
      </nav>
  );
}
